import { cloneDeep } from 'lodash';
import {
    fromEventPattern,
    interval,
    Observable,
    queueScheduler,
    SchedulerLike,
    Subject,
} from 'rxjs';
import { delayWhen, filter, first, map, observeOn, shareReplay, skipWhile } from 'rxjs/operators';
import {
    EventObject,
    interpret,
    Interpreter,
    State,
    StateMachine,
    StateSchema as BaseStateSchema,
} from 'xstate';

/**
 * An ObservableService is an observable wrapper around a state machine service.
 * It handle the initialization of the service, and handle the dispatch of event in it.
 */
export abstract class ObservableService<
    Context extends Record<string, any> = Record<string, any>,
    Event extends EventObject = EventObject,
    StateSchema extends BaseStateSchema = any
> {
    get state() {
        return this.lastState;
    }

    get pipe(): Observable<[State<Context, Event>, Event]>['pipe'] {
        return this.state$.pipe.bind(this.state$);
    }

    get subscribe(): Observable<[State<Context, Event>, Event]>['subscribe'] {
        return this.state$.subscribe.bind(this.state$);
    }

    public readonly scheduler: SchedulerLike;
    private state$: Observable<[State<Context, Event>, Event]>;
    private lastState: State<Context, Event>;

    private service: Interpreter<Context, StateSchema, Event>;
    private event$ = new Subject<Event | Event['type']>();
    private eventQueue: Array<Event | Event['type']> = [];

    constructor(
        machine: StateMachine<Context, StateSchema, Event>,
        scheduler: SchedulerLike = queueScheduler,
    ) {
        this.scheduler = scheduler;
        this.service = interpret(machine);

        this.start();
    }

    public dispose() {
        this.service.stop();
        this.event$.complete();
    }

    public restart() {
        this.service.stop();
        this.start();
    }

    public sendEvent(event: Event | Event['type']) {
        this.event$.next(event);
    }

    public waitFor$(stateValue: keyof StateSchema['states']) {
        return this.state$.pipe(
            filter(value => {
                const [state] = value;
                return (state.value as keyof StateSchema['states']) === stateValue;
            }),
            first(),
        );
    }

    // tslint:disable-next-line:no-empty
    protected boot(): void {}

    private start() {
        this.state$ = fromEventPattern<[State<Context, Event>, Event]>(
            handler => {
                this.service.onTransition(handler).start();
                return this.service;
            },
            (_, service) => service.stop(),
        ).pipe(
            observeOn(this.scheduler),
            // we want to store the last emission, to ensure its new subscriber get notified with it
            // this way, late subscribers don't get out of sync
            shareReplay<[State<Context, Event>, Event]>(1),
            map(value => cloneDeep(value)),
        );

        const serviceInitialized$ = interval(1, this.scheduler).pipe(
            skipWhile(() => {
                return !this.service.initialized;
            }),
            first(),
        );

        serviceInitialized$.subscribe(() => {
            if (this.boot) {
                this.boot();
            }
        });

        this.event$ = new Subject<Event | Event['type']>();

        // we don't link the event observable to the scheduler as it is just a queue to handle service initialization. Btw, the transition will be
        // performed within the scheduler
        this.event$.pipe(delayWhen(() => serviceInitialized$)).subscribe(event => {
            this.service.send(event);
        });

        // to start the observable we need at least one observer on it
        this.state$.subscribe(value => {
            this.lastState = value[0];
        });
    }
}
